import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'fa' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  dir: 'rtl' | 'ltr';
}

const translations: Record<Language, Record<string, string>> = {
  fa: {
    // Navigation
    'nav.products': 'محصولات چیت',
    'nav.pricing': 'تعرفه و خرید',
    'nav.status': 'وضعیت سرور',
    'nav.purchase': 'خرید فوری',
    'nav.blog': 'مجله گیمینگ',
    
    // Hero
    'hero.badge': 'نسل جدید چیت‌های هوشمند',
    'hero.title': 'Ziqo',
    'hero.subtitle': 'قدرت، امنیت و عملکرد بی‌نظیر',
    'hero.description': 'با پیشرفته‌ترین تکنولوژی‌های روز دنیا، تجربه گیمینگ خود را به سطح جدیدی ببرید. بدون نگرانی از بن شدن، با خیال راحت بازی کنید.',
    'hero.cta': 'مشاهده محصولات',
    'hero.secondary': 'چرا Ziqo؟',
    
    // Products
    'product.selector.title': 'انتخاب چیت مورد نظر',
    'product.vmp.title': 'چیت VMP',
    'product.vmp.desc': 'چیت اختصاصی VMP شامل ایم‌بات، وال‌هک و رادار با بایپس قوی آنتی‌چیت.',
    'product.fivem.title': 'چیت FiveM',
    'product.fivem.desc': 'منوی قدرتمند FiveM با قابلیت‌های گادمود، پول بی‌نهایت و ایم‌بات برای رول‌پلی.',
    'product.irfive.title': 'IRfive',
    'product.irfive.desc': 'چیت مخصوص سرورهای ایرانی FiveM با بای‌پس قوی',
    'product.cs2.title': 'چیت CS2',
    'product.cs2.desc': 'چیت کانتر ۲ (CS2) شامل ایم‌بات لژیت، وال‌هک و اسکین‌چنجر بدون خطر بن.',
    'product.select': 'انتخاب محصول',
    'product.features': 'امکانات',
    'product.buy': 'خرید آنلاین',
    'product.toman': 'تومان',
    
    // Pricing
    'pricing.title': 'تعرفه‌های خرید چیت',
    'pricing.subtitle': 'بهترین قیمت چیت در ایران با تحویل آنی',
    'pricing.flexible': 'قیمت‌گذاری منعطف',
    'pricing.badge': 'پیشنهاد ویژه',
    'pricing.day': '۱ روزه (تست)',
    'pricing.week': '۱ هفته‌ای',
    'pricing.month': '۱ ماهه (ویژه)',
    'pricing.lifetime': 'دائمی (نامحدود)',
    'pricing.cta': 'خرید و تحویل آنی',
    'pricing.features': 'دسترسی کامل به تمام امکانات',
    'pricing.toman': 'تومان',
    
    // Status
    'status.title': 'وضعیت سرورها و آپدیت‌ها',
    'status.undetected': 'امن و فعال (غیرقابل شناسایی)',
    'status.detected': 'در حال بروزرسانی',
    'status.lastUpdate': 'آخرین آپدیت',
    'status.uptime': 'پایداری سرور',
    'status.allSystems': 'همه سرویس‌ها فعال و امن هستند',
    'status.vacSafe': 'وضعیت آنتی‌چیت: کاملاً امن',
    'status.noDetections': 'بدون گزارش بن در ۳۶۵ روز گذشته',
    
    // Auth
    'auth.login': 'ورود به پنل کاربری',
    'auth.signup': 'ثبت نام در سایت',
    'auth.username': 'نام کاربری',
    'auth.password': 'رمز عبور',
    'auth.confirmPassword': 'تکرار رمز عبور',
    'auth.noAccount': 'هنوز ثبت نام نکرده‌اید؟',
    'auth.hasAccount': 'قبلاً ثبت نام کرده‌اید؟',
    'auth.back': 'بازگشت به صفحه اصلی',
    'auth.welcome': 'خوش آمدید',
    'auth.createAccount': 'ساخت حساب کاربری',
    'auth.loginSubtitle': 'برای دسترسی به محصولات وارد شوید',
    'auth.signupSubtitle': 'به جمع حرفه‌ای‌ها بپیوندید',
    'auth.signingIn': 'در حال ورود...',
    'auth.signingUp': 'در حال ساخت حساب...',
    'auth.captcha': 'کد امنیتی',
    'auth.captchaRequired': 'لطفاً کد امنیتی را وارد کنید',
    'auth.success': 'ورود موفقیت‌آمیز بود!',
    'auth.created': 'حساب کاربری با موفقیت ساخته شد!',
    'auth.usernameTaken': 'این نام کاربری قبلاً انتخاب شده است',
    
    // Validation
    'validation.username.required': 'نام کاربری الزامی است',
    'validation.username.min': 'نام کاربری باید حداقل ۳ حرف باشد',
    'validation.username.regex': 'نام کاربری فقط شامل حروف انگلیسی و اعداد باشد',
    'validation.password.required': 'رمز عبور الزامی است',
    'validation.password.min': 'رمز عبور باید حداقل ۶ کاراکتر باشد',
    'validation.password.match': 'رمز عبور و تکرار آن مطابقت ندارند',
    
    // About
    'about.title': 'درباره Ziqo',
    'about.subtitle': 'تیم تخصصی توسعه چیت و امنیت گیمینگ',
    'about.security': 'امنیت تضمینی',
    'about.securityDesc': 'محافظت در سطح کرنل',
    'about.community': 'جامعه کاربری',
    'about.communityDesc': 'بیش از ۵۰۰۰ کاربر فعال ایرانی',
    'about.performance': 'عملکرد بهینه',
    'about.performanceDesc': 'بدون هیچگونه افت FPS در بازی',
    'about.desc': 'تیم Ziqo با بهره‌گیری از متخصصین امنیت سایبری، قوی‌ترین و امن‌ترین ابزارهای تقلب (Cheat) را برای بازی‌های آنلاین ارائه می‌دهد. هدف ما ارائه تجربه‌ای لذت‌بخش و بدون استرس بن شدن برای گیمرهای ایرانی است.',
    
    // Contact
    'contact.title': 'تماس با پشتیبانی',
    'contact.subtitle': 'سوالی دارید؟ تیم پشتیبانی ما آماده پاسخگویی است',
    'contact.name': 'نام شما',
    'contact.email': 'ایمیل شما',
    'contact.message': 'متن پیام',
    'contact.send': 'ارسال پیام',
    'contact.sending': 'در حال ارسال...',
    'contact.sent': 'پیام شما با موفقیت ارسال شد!',
    
    // Purchase Sheet
    'purchase.title': 'انتخاب روش پرداخت',
    'purchase.crypto': 'ارز دیجیتال (تتر/بیت‌کوین)',
    'purchase.cryptoDesc': 'پرداخت امن و ناشناس با ارزهای دیجیتال',
    'purchase.rial': 'درگاه بانکی (تومان)',
    'purchase.rialDesc': 'پرداخت با کلیه کارت‌های عضو شتاب',
    'purchase.selectPlan': 'پلن انتخاب شده',
    
    // Footer
    'footer.rights': 'تمامی حقوق محفوظ است.',
    'footer.disclaimer': 'مسئولیت استفاده از چیت بر عهده کاربر است.',
    'footer.products': 'محصولات ما',
    'footer.links': 'دسترسی سریع',
    'footer.support': 'پشتیبانی مشتریان',
    'footer.about': 'درباره ما',
    'footer.contact': 'تماس با ما',
    'footer.terms': 'قوانین و مقررات',
    'footer.privacy': 'حریم خصوصی',
    
    // Theme
    'theme.light': 'روز',
    'theme.dark': 'شب',

    // Dashboard
    'dashboard.title': 'داشبورد',
    'dashboard.home': 'صفحه اصلی',
    'dashboard.logout': 'خروج',
    'dashboard.changePassword': 'تغییر رمز عبور',
  },
  en: {
    // Navigation
    'nav.products': 'Products',
    'nav.pricing': 'Pricing',
    'nav.status': 'Server Status',
    'nav.purchase': 'Buy Now',
    'nav.blog': 'Gaming Blog',
    
    // Hero
    'hero.badge': 'Next-Gen Smart Cheats',
    'hero.title': 'Ziqo',
    'hero.subtitle': 'Power, Security & Unmatched Performance',
    'hero.description': 'Take your gaming experience to the next level with the most advanced technologies. Play worry-free without the fear of getting banned.',
    'hero.cta': 'View Products',
    'hero.secondary': 'Why Ziqo?',
    
    // Products
    'product.selector.title': 'Select Your Cheat',
    'product.vmp.title': 'VMP Cheat',
    'product.vmp.desc': 'Exclusive VMP cheat with aimbot, wallhack, and radar with strong anti-cheat bypass.',
    'product.fivem.title': 'FiveM Cheat',
    'product.fivem.desc': 'Powerful FiveM menu with godmode, unlimited money, and aimbot for roleplay.',
    'product.irfive.title': 'IRfive',
    'product.irfive.desc': 'Cheat for Iranian FiveM servers with strong bypass',
    'product.cs2.title': 'CS2 Cheat',
    'product.cs2.desc': 'Counter-Strike 2 cheat with legit aimbot, wallhack, and skin changer without ban risk.',
    'product.select': 'Select Product',
    'product.features': 'Features',
    'product.buy': 'Buy Online',
    'product.toman': 'Toman',
    
    // Pricing
    'pricing.title': 'Cheat Pricing Plans',
    'pricing.subtitle': 'Best cheat prices with instant delivery',
    'pricing.flexible': 'Flexible Pricing',
    'pricing.badge': 'Special Offer',
    'pricing.day': '1 Day (Trial)',
    'pricing.week': '1 Week',
    'pricing.month': '1 Month (Special)',
    'pricing.lifetime': 'Lifetime (Unlimited)',
    'pricing.cta': 'Buy & Instant Delivery',
    'pricing.features': 'Full access to all features',
    'pricing.toman': 'Toman',
    
    // Status
    'status.title': 'Server Status & Updates',
    'status.undetected': 'Safe & Active (Undetected)',
    'status.detected': 'Updating',
    'status.lastUpdate': 'Last Update',
    'status.uptime': 'Server Uptime',
    'status.allSystems': 'All systems are active and secure',
    'status.vacSafe': 'Anti-cheat Status: Fully Safe',
    'status.noDetections': 'No ban reports in the last 365 days',
    
    // Auth
    'auth.login': 'Login to Panel',
    'auth.signup': 'Sign Up',
    'auth.username': 'Username',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.back': 'Back to Home',
    'auth.welcome': 'Welcome Back',
    'auth.createAccount': 'Create Account',
    'auth.loginSubtitle': 'Sign in to access products',
    'auth.signupSubtitle': 'Join the professionals',
    'auth.signingIn': 'Signing in...',
    'auth.signingUp': 'Creating account...',
    'auth.captcha': 'Security Code',
    'auth.captchaRequired': 'Please enter the security code',
    'auth.success': 'Login successful!',
    'auth.created': 'Account created successfully!',
    'auth.usernameTaken': 'This username is already taken',
    
    // Validation
    'validation.username.required': 'Username is required',
    'validation.username.min': 'Username must be at least 3 characters',
    'validation.username.regex': 'Username can only contain letters and numbers',
    'validation.password.required': 'Password is required',
    'validation.password.min': 'Password must be at least 6 characters',
    'validation.password.match': 'Passwords do not match',
    
    // About
    'about.title': 'About Ziqo',
    'about.subtitle': 'Expert Cheat Development & Gaming Security Team',
    'about.security': 'Guaranteed Security',
    'about.securityDesc': 'Kernel-level protection',
    'about.community': 'Community',
    'about.communityDesc': 'Over 5000 active users',
    'about.performance': 'Optimized Performance',
    'about.performanceDesc': 'Zero FPS drop in games',
    'about.desc': 'The Ziqo team, leveraging cybersecurity experts, provides the strongest and most secure cheat tools for online games. Our goal is to deliver an enjoyable and stress-free gaming experience.',
    
    // Contact
    'contact.title': 'Contact Support',
    'contact.subtitle': 'Have a question? Our support team is ready to help',
    'contact.name': 'Your Name',
    'contact.email': 'Your Email',
    'contact.message': 'Message',
    'contact.send': 'Send Message',
    'contact.sending': 'Sending...',
    'contact.sent': 'Your message has been sent successfully!',
    
    // Purchase Sheet
    'purchase.title': 'Select Payment Method',
    'purchase.crypto': 'Cryptocurrency (USDT/BTC)',
    'purchase.cryptoDesc': 'Secure and anonymous payment with crypto',
    'purchase.rial': 'Bank Transfer (Toman)',
    'purchase.rialDesc': 'Pay with all Shetab member cards',
    'purchase.selectPlan': 'Selected Plan',
    
    // Footer
    'footer.rights': 'All rights reserved.',
    'footer.disclaimer': 'Use of cheats is at your own risk.',
    'footer.products': 'Our Products',
    'footer.links': 'Quick Access',
    'footer.support': 'Customer Support',
    'footer.about': 'About Us',
    'footer.contact': 'Contact Us',
    'footer.terms': 'Terms & Conditions',
    'footer.privacy': 'Privacy Policy',
    
    // Theme
    'theme.light': 'Light',
    'theme.dark': 'Dark',

    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.home': 'Home',
    'dashboard.logout': 'Logout',
    'dashboard.changePassword': 'Change Password',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('fa');

  useEffect(() => {
    document.documentElement.dir = language === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.lang = language === 'fa' ? 'fa' : 'en';
  }, [language]);

  const t = (key: string): string => {
    return translations[language]?.[key] || translations['fa']?.[key] || key;
  };

  const dir = language === 'fa' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
